import ContentManagementInterface, {
  ContentItem,
} from '../../../models/ContentManagementInterface';
import { ContentProps } from '../../../models/TopLevelTemplateComponentProps';
import {
  generateHeroImageType,
  generateImageCarouselType,
  generateLeftRightListType,
  generateStandardHeaderTextType,
  generateSubtitleType,
  generateTableType,
  generateTextType,
  newContentId,
} from '../../../services/TemplateUtils';
import { PageMode } from './newContentManagementEditorTypes';

const MAIN_LAYOUT_HEADER_HEIGHT_PX = 80;

/** Block types whose properties carry a `footnotes` array (see `TopLevelTemplateContentProps`). */
const FOOTNOTE_CAPABLE_TYPES: ReadonlySet<ContentItem['type']> = new Set([
  'StandardHeaderText',
  'LeftRightList',
  'Table',
  'ImageCarousel',
]);

export function blockSupportsFootnotes(type: ContentItem['type']): boolean {
  return FOOTNOTE_CAPABLE_TYPES.has(type);
}

/**
 * Whether a block's Footnotes panel should show, given any explicit open/closed override
 * (toggled from its toolbar button) — defaults to open only when the block already has
 * footnotes, so existing content is never hidden on load.
 */
export function isFootnotesOpen(
  block: ContentItem,
  footnotesOpenByBlockId: Record<string, boolean>,
): boolean {
  if (block.id in footnotesOpenByBlockId) return footnotesOpenByBlockId[block.id];
  if (!blockSupportsFootnotes(block.type)) return false;
  const footnotes = (block.properties as { footnotes?: unknown[] }).footnotes;
  return (footnotes?.length ?? 0) > 0;
}

/** StandardHeaderText preview DOM id uses block.properties.id; others use block.id. */
export function getPreviewDomId(block: ContentItem): string {
  return block.type === 'StandardHeaderText' ? (block.properties as { id: string }).id : block.id;
}

export function getScrollOffset(toolbarHeight: number): number {
  return MAIN_LAYOUT_HEADER_HEIGHT_PX + toolbarHeight + 8;
}

export function findCurrentBlock(
  blocks: ContentItem[],
  mode: PageMode,
  scrollOffset: number,
): ContentItem | null {
  const threshold = scrollOffset + 20;
  let best: ContentItem | null = null;
  for (const block of blocks) {
    const domId = mode === 'edit' ? `editor-block-${block.id}` : getPreviewDomId(block);
    const el = document.getElementById(domId);
    if (!el) continue;
    if (el.getBoundingClientRect().top <= threshold) best = block;
  }
  return best;
}

export function getScrollTargetId(block: ContentItem, pageMode: PageMode): string {
  return pageMode === 'preview-web' ? getPreviewDomId(block) : `editor-block-${block.id}`;
}

export function createEmptyContent(
  fundOpportunityId: string,
  name: string,
): ContentManagementInterface {
  return {
    name,
    relatedObject: 'FundOpportunity',
    relatedObjectId: fundOpportunityId,
    content: [],
  };
}

export function createContentBlock(
  type: ContentItem['type'],
  modelId: string,
  modelType: string = 'FundOpportunity',
): ContentItem {
  switch (type) {
    case 'Subtitle':
      return generateSubtitleType();
    case 'Text':
      return generateTextType();
    case 'HeroImage':
      return generateHeroImageType();
    case 'LeftRightList':
      return generateLeftRightListType();
    case 'ImageCarousel': {
      // Start empty — the editor shows only the "add a slide" placeholder;
      // slides are created by giving that placeholder an image.
      const base = generateImageCarouselType(modelId, modelType);
      return { ...base, properties: { ...base.properties, items: [] } };
    }
    case 'Table': {
      // Start empty — the editor shows a placeholder until "Create table".
      const base = generateTableType();
      return { ...base, properties: { ...base.properties, rows: [] } };
    }
    default:
      return generateStandardHeaderTextType();
  }
}

export function getLeftRightListItemText(text: ContentProps | ContentProps[] | undefined): string {
  if (!text) return '';
  if (Array.isArray(text)) return text.map((entry) => entry.content).join('\n');
  return text.content;
}

export function updateLeftRightListItemText(
  existing: ContentProps | ContentProps[] | undefined,
  value: string,
): ContentProps {
  const textId = Array.isArray(existing)
    ? (existing[0]?.id ?? newContentId())
    : (existing?.id ?? newContentId());
  return { id: textId, content: value };
}

/** Recursively collects every string `id` found anywhere in a block tree (headings, items, cells, ...). */
export function collectContentIds(value: unknown, into: Set<string> = new Set()): Set<string> {
  if (Array.isArray(value)) {
    value.forEach((entry) => collectContentIds(entry, into));
  } else if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (typeof obj.id === 'string' && obj.id) into.add(obj.id);
    Object.values(obj).forEach((entry) => collectContentIds(entry, into));
  }
  return into;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/** A `ContentItem`-shaped block: `{ type, properties, id? }` — e.g. one built by
 * hand as `{ type, properties } as ContentItem` with the `id` key omitted entirely. */
function isBlockShaped(v: unknown): boolean {
  return isPlainObject(v) && typeof v.type === 'string' && isPlainObject(v.properties);
}

/**
 * True when a `content` key's value is a leaf (`ContentProps`/heading/cell shape,
 * e.g. `{ id, content: 'text' }`) rather than a top-level `content: ContentItem[]`
 * array of whole blocks, which must not itself be treated as needing an id.
 */
function contentValueIsLeaf(value: unknown): boolean {
  if (typeof value === 'string') return true;
  if (isPlainObject(value)) return !isBlockShaped(value);
  if (Array.isArray(value)) return !value.some(isBlockShaped);
  return false;
}

/** Whether a node is one that should carry an `id` — present, missing, or not. */
function isIdBearingNode(record: Record<string, unknown>): boolean {
  return (
    'id' in record ||
    isBlockShaped(record) ||
    ('content' in record && contentValueIsLeaf(record.content)) ||
    'chartData' in record
  );
}

/**
 * Recursively backfills a fresh id onto every node that should carry one — present
 * but missing/duplicate, or absent entirely (a hand-built `{ type, properties }`
 * block, or an AI-extracted section, may omit the `id` key at any depth) — needed
 * so nothing renders with a duplicate/undefined React key downstream. Mutates
 * `seen` with every id kept or generated.
 */
export function assignMissingIds<T>(value: T, seen: Set<string>): T {
  if (Array.isArray(value)) {
    return value.map((entry) => assignMissingIds(entry, seen)) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const obj: Record<string, unknown> = { ...(value as Record<string, unknown>) };
    Object.keys(obj).forEach((key) => {
      obj[key] = assignMissingIds(obj[key], seen);
    });
    if (isIdBearingNode(obj)) {
      const current = obj.id;
      if (typeof current !== 'string' || !current || seen.has(current)) {
        obj.id = newContentId();
      }
      seen.add(obj.id as string);
    }
    return obj as T;
  }
  return value;
}

/**
 * Splices `sections` into `content` at `atIndex` (end of the list by default — the Documents tab
 * and Upload Document flow's unscoped import always append). The always-visible smart-paste zone
 * passes `0` to prepend instead, since it sits above any existing blocks.
 */
export function appendContentSections(
  content: ContentManagementInterface,
  sections: ContentItem[],
  atIndex: number = content.content.length,
): { content: ContentManagementInterface; insertedIds: string[] } {
  const usedIds = collectContentIds(content.content);
  const fresh = sections.map((section) => assignMissingIds(section, usedIds));
  const blocks = [...content.content];
  blocks.splice(atIndex, 0, ...fresh);
  return {
    content: { ...content, content: blocks },
    insertedIds: fresh.map((block) => block.id),
  };
}

export function getTableCellContent(cell: { content: ContentProps | ContentProps[] }): string {
  if (Array.isArray(cell.content)) {
    return cell.content.map((entry: ContentProps) => entry.content).join(' ');
  }
  return cell.content.content;
}

export function updateTableCellContent(
  existing: ContentProps | ContentProps[],
  value: string,
): ContentProps {
  const cellId = Array.isArray(existing)
    ? (existing[0]?.id ?? newContentId())
    : ((existing as ContentProps).id ?? newContentId());
  return { id: cellId, content: value };
}
